import { useEffect, useRef } from 'react';
import { useShowToast } from '@/hooks/useShowToast';
import { useUnderwayStore } from './useUnderwayStore';
import { useUnderwayDispatcher } from './useUnderwayDispatcher';
import { UNDERWAY_POPUP_STRINGS } from './underwayPopup.strings';

// Manages the setTimeout lifecycle for each pending activity. Mounted
// once at app shell level. We can't persist timeout IDs across
// refreshes, so on mount we read each pending activity's `createdAt`
// and schedule a new timeout for the elapsed-adjusted remaining time.
// Same approach works for pause → resume (resume re-anchors createdAt)
// and Stop → dismiss.
//
// Terminal rows (completed / cancelled / failed) are intentionally NOT
// auto-dismissed — a fired automation stays visible as confirmation
// until the user dismisses the row (×) or closes the popup.
export const UnderwayTimerManager = () => {
  const activities = useUnderwayStore((s) => s.activities);
  const setStatus = useUnderwayStore((s) => s.setStatus);
  const { fire } = useUnderwayDispatcher();
  const toast = useShowToast();

  // Map<activityId, timeoutId>. Outlives renders so we don't double-
  // schedule or leak timers when the activity list re-renders for
  // unrelated reasons (other activity's tick, etc.).
  const firingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  useEffect(() => {
    const firing = firingTimersRef.current;
    const pendingIds = new Set<string>();

    activities.forEach((a) => {
      // Schedule the move dispatch for pending activities.
      if (a.status === 'pending') {
        pendingIds.add(a.id);
        if (!firing.has(a.id)) {
          const elapsed = Date.now() - a.createdAt;
          const remaining = Math.max(0, a.delayMs - elapsed);
          const timer = setTimeout(() => {
            firing.delete(a.id);
            setStatus(a.id, 'running');
            fire(a)
              .then(() => setStatus(a.id, 'completed'))
              .catch((err: unknown) => {
                setStatus(
                  a.id,
                  'failed',
                  err instanceof Error ? err.message : 'Move failed'
                );
                toast({
                  title: UNDERWAY_POPUP_STRINGS.toasts.triggerFailed,
                  status: 'error',
                });
              });
          }, remaining);
          firing.set(a.id, timer);
        }
      }
    });

    // Cancel any firing timers for activities that left the 'pending'
    // bucket (paused, stopped, removed). The next render re-schedules
    // if needed.
    for (const [id, timer] of firing.entries()) {
      if (!pendingIds.has(id)) {
        clearTimeout(timer);
        firing.delete(id);
      }
    }
  }, [activities, fire, setStatus, toast]);

  // Final cleanup on unmount.
  useEffect(() => {
    const firing = firingTimersRef.current;
    return () => {
      firing.forEach(clearTimeout);
      firing.clear();
    };
  }, []);

  return null;
};
